import React, { useState, useEffect, useMemo } from 'react';
import AdminLayout from '../components/Layout/AdminLayout';
import { FaChartLine, FaCalendarAlt, FaFileAlt, FaBoxOpen } from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';

const SalesReportPage: React.FC = () => {
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Date range state (Default: current month start to end)
  const [startDate, setStartDate] = useState<string>(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1).toLocaleDateString('en-CA')
  );
  const [endDate, setEndDate] = useState<string>(
    new Date().toLocaleDateString('en-CA')
  );

  // 1. Fetch all orders from database
  const fetchAllOrders = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get('https://www.ayushvatika.com/api/orders/all', {

      // const res = await axios.get('http://localhost:8484/api/orders/all', {

        headers: { 'Authorization': `Bearer ${token}` }
      });
      setOrders(res.data);
    } catch (err: any) {
      toast.error("Sales data fetch karne mein error aayi!");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllOrders();
  }, []);

  // 2. Filter Orders by Date Range
  const filteredOrders = useMemo(() => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    // Include the entire end date by setting time to 23:59:59
    end.setHours(23, 59, 59, 999);

    return orders.filter(o => {
      const oDate = new Date(o.orderDate);
      // Only count completed sales (PAID or DELIVERED)
      const isPaid = o.paymentStatus === 'PAID' || o.status === 'DELIVERED';
      return isPaid && oDate >= start && oDate <= end;
    });
  }, [orders, startDate, endDate]);

  // 3. Calculate Aggregated Stats
  const stats = useMemo(() => {
    const totalRevenue = filteredOrders.reduce((sum, o) => sum + o.totalAmount, 0);
    const totalOrders = filteredOrders.length;
    const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

    // Product-wise sales aggregation
    const productSalesMap: Record<string, { qty: number, rev: number }> = {};
    
    filteredOrders.forEach(order => {
        order.orderItems?.forEach((item: any) => {
            if (!productSalesMap[item.productName]) {
                productSalesMap[item.productName] = { qty: 0, rev: 0 };
            }
            productSalesMap[item.productName].qty += item.quantity;
            productSalesMap[item.productName].rev += (item.price * item.quantity);
        });
    });

    // Convert map to sorted array (Top selling first)
    const productPerformance = Object.entries(productSalesMap)
        .map(([name, data]) => ({ name, ...data }))
        .sort((a, b) => b.rev - a.rev);

    return { totalRevenue, totalOrders, avgOrderValue, productPerformance };
  }, [filteredOrders]);

  return (
    <AdminLayout>
      {/* Background: Dynamic Light/Dark */}
      <div className="p-6 min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
        
        {/* Header & Date Filter */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
            <h1 className="text-3xl font-black text-gray-800 dark:text-white tracking-tight flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg text-blue-600 dark:text-blue-400">
                    <FaChartLine size={24}/>
                </div>
                Sales Report
            </h1>
            
            <div className="flex items-center gap-3 bg-white dark:bg-gray-800 p-2 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 px-2">
                    <FaCalendarAlt className="text-gray-400"/>
                    <input 
                        type="date" 
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="text-sm font-bold text-gray-600 dark:text-gray-300 outline-none bg-transparent cursor-pointer"
                    />
                </div>
                <span className="text-gray-300 dark:text-gray-600">|</span>
                <div className="flex items-center gap-2 px-2">
                    <input 
                        type="date" 
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="text-sm font-bold text-gray-600 dark:text-gray-300 outline-none bg-transparent cursor-pointer"
                    />
                </div>
            </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            <ReportCard 
                title="Total Revenue" 
                value={`₹${stats.totalRevenue.toLocaleString('en-IN')}`} 
                gradient="from-green-500 to-emerald-700"
                icon={<FaFileAlt/>}
            />
            <ReportCard 
                title="Total Orders" 
                value={stats.totalOrders} 
                gradient="from-blue-500 to-indigo-700"
                icon={<FaBoxOpen/>}
            />
            <ReportCard 
                title="Avg. Order Value" 
                value={`₹${Math.round(stats.avgOrderValue).toLocaleString('en-IN')}`} 
                gradient="from-purple-500 to-pink-700"
                icon={<FaChartLine/>}
            />
        </div>

        {/* Product Performance Table */}
        <div className="bg-white dark:bg-gray-800 rounded-[2rem] shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div className="p-8 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center">
            <h2 className="text-xl font-black text-gray-800 dark:text-white uppercase tracking-wider">Top Selling Products</h2>
            <span className="text-xs font-bold bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300 px-3 py-1 rounded-full">
                {stats.productPerformance.length} Items
            </span>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="px-8 py-5 text-left text-xs font-black text-gray-400 dark:text-gray-400 uppercase tracking-widest">Product Name</th>
                  <th className="px-8 py-5 text-center text-xs font-black text-gray-400 dark:text-gray-400 uppercase tracking-widest">Quantity Sold</th>
                  <th className="px-8 py-5 text-right text-xs font-black text-gray-400 dark:text-gray-400 uppercase tracking-widest">Revenue Generated</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {loading ? (
                  <tr><td colSpan={3} className="px-8 py-20 text-center text-gray-400 dark:text-gray-500 italic">Calculating sales data...</td></tr>
                ) : stats.productPerformance.length > 0 ? (
                  stats.productPerformance.map((p, idx) => (
                    <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group">
                      <td className="px-8 py-5">
                        <p className="text-sm font-bold text-gray-800 dark:text-gray-200 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">{p.name}</p>
                      </td>
                      <td className="px-8 py-5 text-center">
                        <span className="px-4 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-sm font-black text-gray-600 dark:text-gray-300">{p.qty}</span>
                      </td>
                      <td className="px-8 py-5 text-right">
                        <p className="text-lg font-black text-gray-900 dark:text-white tracking-tight">₹{p.rev.toLocaleString('en-IN')}</p>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3} className="px-8 py-20 text-center text-gray-400 dark:text-gray-500 font-bold">
                      No sales recorded for the selected period.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

// Reusable Summary Card Component
const ReportCard = ({ title, value, gradient, icon }: any) => (
  <div className={`bg-gradient-to-br ${gradient} p-8 rounded-[2rem] text-white shadow-2xl relative overflow-hidden group hover:scale-[1.02] transition-all`}>
    <div className="absolute -right-4 -bottom-4 opacity-10 group-hover:scale-125 transition-transform duration-500 text-white">
        {React.cloneElement(icon, { size: 120 })}
    </div>
    <div className="relative z-10">
        <p className="text-xs font-black uppercase tracking-[0.2em] opacity-80 mb-2">{title}</p>
        <h3 className="text-4xl font-black tracking-tight">{value}</h3>
    </div>
  </div>
);

export default SalesReportPage;